// All widget CSS, inlined into the shadow root (docs/widget.md §3): no external
// stylesheets or font fetches, ever. Theme lands via CSS custom properties set
// on the shadow host (--cb-primary, --cb-z). Persian-friendly system font stack;
// Vazirmatn is used only if the HOST page already provides it.

export const WIDGET_CSS = `
:host {
  all: initial;
}
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.cb-root {
  font-family: Vazirmatn, -apple-system, BlinkMacSystemFont, 'Segoe UI', Tahoma, Arial, sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: #1f2430;
  -webkit-font-smoothing: antialiased;
}
/* :where() zeroes this reset's specificity so component classes (.cb-submit, .cb-launcher, …)
   always win on their own background/color/border — without it, the class+element selector
   here (0-1-1) outranks every single-class component selector (0-1-0) and silently strips
   their colors regardless of source order. */
.cb-root :where(button) { font: inherit; cursor: pointer; border: 0; background: none; color: inherit; }
.cb-root :where(textarea, input) { font: inherit; color: inherit; }

/* ---------- launcher ---------- */
.cb-launcher {
  position: fixed;
  bottom: var(--cb-margin-y);
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--cb-primary);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 24px rgba(15, 23, 42, .28);
  z-index: var(--cb-z);
  transition: transform .15s ease;
}
.cb-launcher:hover { transform: scale(1.06); }
.cb-launcher:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }

/* ---------- chat panel ---------- */
.cb-panel {
  position: fixed;
  bottom: calc(var(--cb-margin-y) + 68px);
  width: 378px;
  max-width: calc(100vw - 24px);
  height: 560px;
  max-height: calc(100vh - var(--cb-margin-y) - 90px);
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 12px 48px rgba(15, 23, 42, .24);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: var(--cb-z);
  animation: cb-pop .18s ease-out;
}
@keyframes cb-pop { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }

.cb-header {
  background: var(--cb-primary);
  color: #fff;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
}
.cb-header .cb-title { font-weight: 700; font-size: 15px; flex: 1 1 auto; }
.cb-header .cb-sub { font-size: 11px; opacity: .85; }
.cb-iconbtn {
  width: 32px; height: 32px;
  border-radius: 8px;
  display: inline-flex; align-items: center; justify-content: center;
  color: inherit;
  flex: 0 0 auto;
}
.cb-iconbtn:hover { background: rgba(255,255,255,.16); }
.cb-iconbtn:focus-visible { outline: 2px solid currentColor; outline-offset: 1px; }

/* ---------- shared pinned message ---------- */
.cb-pinned {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 8px 14px;
  background: #fff;
  border-bottom: 1px solid #e6e8ef;
  text-align: start;
  width: 100%;
}
.cb-pinned:hover { background: #f4f6fb; }
.cb-pin-ico { color: var(--cb-primary); display: inline-flex; flex: 0 0 auto; }
.cb-pin-body { display: flex; flex-direction: column; min-width: 0; line-height: 1.45; }
.cb-pin-label { font-size: 10.5px; color: var(--cb-primary); font-weight: 700; }
.cb-pin-text { font-size: 12.5px; color: #333; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cb-flash { animation: cb-flash 1.4s ease-out; }
@keyframes cb-flash {
  0%, 25% { box-shadow: 0 0 0 3px var(--cb-primary); }
  100% { box-shadow: 0 0 0 0 transparent; }
}

.cb-msgs {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #f8f9fb;
}
.cb-msg {
  max-width: 82%;
  padding: 9px 13px;
  border-radius: 14px;
  white-space: pre-wrap;
  word-break: break-word;
}
.cb-msg.v { align-self: flex-end; background: var(--cb-primary); color: #fff; border-bottom-left-radius: 4px; }
.cb-msg.a { align-self: flex-start; background: #fff; border: 1px solid #e6e8ef; border-bottom-right-radius: 4px; }
.cb-root[dir="ltr"] .cb-msg.v { border-radius: 14px 14px 4px 14px; }
.cb-root[dir="ltr"] .cb-msg.a { border-radius: 14px 14px 14px 4px; }
.cb-msg.err { align-self: center; background: #fdecec; color: #b3261e; border: 1px solid #f5c9c6; font-size: 13px; text-align: center; }
.cb-retry { display: block; margin: 6px auto 0; color: #b3261e; font-weight: 700; text-decoration: underline; }
.cb-link { color: var(--cb-primary); text-decoration: underline; word-break: break-all; }
.cb-msg.v .cb-link { color: #fff; }

/* ---------- operator avatars (roster + per-reply) ---------- */
.cb-avatar {
  width: 28px; height: 28px;
  border-radius: 50%;
  flex: 0 0 auto;
  display: inline-flex; align-items: center; justify-content: center;
  position: relative;
  overflow: hidden;
  color: #fff;
  font-size: 11px; font-weight: 700;
  user-select: none;
}
.cb-avatar-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }

/* header roster — overlapping stack, white-ringed to pop on the primary header */
.cb-roster { display: inline-flex; align-items: center; flex: 0 0 auto; }
.cb-roster .cb-avatar { border: 2px solid rgba(255, 255, 255, .85); }
.cb-roster .cb-avatar + .cb-avatar { margin-inline-start: -10px; }
.cb-avatar-more { background: rgba(255, 255, 255, .28); font-size: 10px; }

/* operator (human) reply — avatar next to a bot-styled bubble, on the assistant side */
.cb-msgrow.o { align-self: flex-start; display: flex; align-items: flex-end; gap: 8px; max-width: 90%; }
.cb-msgrow.o .cb-avatar { width: 26px; height: 26px; margin-bottom: 2px; }
.cb-msg.o { background: #fff; border: 1px solid #dfe3ee; border-bottom-right-radius: 4px; }
.cb-root[dir="ltr"] .cb-msg.o { border-radius: 14px 14px 14px 4px; }
.cb-msg.o .cb-msg-author { display: block; font-size: 11px; font-weight: 700; color: var(--cb-primary); margin-bottom: 2px; }

/* ---------- operator-sent form card (fill in the chat) ---------- */
.cb-formcard { width: 300px; max-width: 100%; white-space: normal; padding: 12px 13px; }
.cb-formcard-title { font-size: 13.5px; font-weight: 800; margin-bottom: 4px; }
.cb-formcard-desc { font-size: 12px; color: #667; margin-bottom: 10px; }
.cb-formcard .cb-form { margin-bottom: 10px; }
.cb-formcard .cb-survey-questions .cb-q:last-child { margin-bottom: 10px; }
.cb-formsubmit {
  width: 100%; padding: 9px 14px; border-radius: 10px;
  background: var(--cb-primary); color: #fff; font-size: 13px; font-weight: 700;
  border: 0; cursor: pointer;
}
.cb-formsubmit:hover { filter: brightness(1.08); }
.cb-formsubmit:disabled { opacity: .55; cursor: default; filter: none; }
.cb-formcard-done { display: flex; align-items: center; gap: 6px; color: #1a7f4b; font-size: 13px; font-weight: 700; }
.cb-formcard-done svg { flex-shrink: 0; }

/* ---------- message attachments (operator media / quick replies) ---------- */
.cb-att { display: block; margin-bottom: 6px; }
.cb-att:last-child { margin-bottom: 0; }
.cb-att-link { display: block; }
.cb-att-img { max-width: 100%; max-height: 220px; border-radius: 10px; object-fit: cover; cursor: pointer; }
.cb-att-audio { width: 220px; max-width: 100%; height: 40px; }
.cb-att-file {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 10px; border-radius: 10px;
  background: rgba(0, 0, 0, .04); border: 1px solid #e6e8ef;
  color: inherit; text-decoration: none; font-size: 13px; font-weight: 600;
  word-break: break-all;
}
.cb-msg.v .cb-att-file { background: rgba(255, 255, 255, .18); border-color: rgba(255, 255, 255, .3); }

.cb-typing { display: inline-flex; gap: 4px; padding: 4px 2px; }
.cb-typing i {
  width: 6px; height: 6px; border-radius: 50%;
  background: #9aa1b1;
  animation: cb-blink 1.2s infinite;
}
.cb-typing i:nth-child(2) { animation-delay: .2s; }
.cb-typing i:nth-child(3) { animation-delay: .4s; }
@keyframes cb-blink { 0%, 80%, 100% { opacity: .25; } 40% { opacity: 1; } }

.cb-chips { display: flex; flex-wrap: wrap; gap: 6px; padding: 0 4px; }
.cb-chip {
  border: 1px solid var(--cb-primary);
  color: var(--cb-primary);
  border-radius: 99px;
  padding: 5px 12px;
  font-size: 13px;
  background: #fff;
}
.cb-chip:hover { background: var(--cb-primary); color: #fff; }

.cb-inputrow {
  flex: 0 0 auto;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid #e6e8ef;
  background: #fff;
}
.cb-input {
  flex: 1 1 auto;
  border: 1px solid #d9dce4;
  border-radius: 10px;
  padding: 8px 12px;
  resize: none;
  max-height: 96px;
  min-height: 38px;
  background: #fff;
  outline: none;
}
.cb-input:focus { border-color: var(--cb-primary); }
.cb-send {
  width: 38px; height: 38px;
  border-radius: 10px;
  background: var(--cb-primary);
  color: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  flex: 0 0 auto;
}
.cb-send:disabled { opacity: .45; cursor: default; }
.cb-root[dir="rtl"] .cb-send svg { transform: scaleX(-1); }

/* pre-chat contact gate (config.requireContactInfo) */
.cb-gate {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border-top: 1px solid #e6e8ef;
  background: #fff;
}
.cb-gate-title { font-size: 13px; color: #4b5162; line-height: 1.6; margin-bottom: 2px; }
.cb-gate-input {
  border: 1px solid #d9dce4;
  border-radius: 10px;
  padding: 9px 12px;
  font: inherit;
  background: #fff;
  outline: none;
}
.cb-gate-input:focus { border-color: var(--cb-primary); }
.cb-gate-submit {
  border-radius: 10px;
  padding: 10px 12px;
  background: var(--cb-primary);
  color: #fff;
  font-weight: 700;
  cursor: pointer;
}
.cb-gate-submit:hover { filter: brightness(1.08); }
.cb-gate-submit:disabled { opacity: .55; cursor: default; filter: none; }

.cb-powered {
  flex: 0 0 auto;
  text-align: center;
  font-size: 10px;
  color: #9aa1b1;
  padding: 3px 0 5px;
  background: #fff;
}

/* ---------- surveys ---------- */
.cb-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, .45);
  z-index: var(--cb-z);
  animation: cb-fade .15s ease-out;
}
@keyframes cb-fade { from { opacity: 0; } to { opacity: 1; } }

.cb-survey {
  position: fixed;
  background: #fff;
  border: 1px solid #eceef3;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, .08), 0 16px 48px rgba(15, 23, 42, .22);
  width: 408px;
  max-width: calc(100vw - 24px);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  z-index: calc(var(--cb-z) + 1);
  padding: 22px 24px 20px;
}
.cb-survey.popup { top: 50%; left: 50%; transform: translate(-50%, -50%); animation: cb-pop .18s ease-out; }
.cb-survey.slide_in { bottom: calc(var(--cb-margin-y) + 68px); animation: cb-slide .25s ease-out; }
@keyframes cb-slide { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: none; } }

.cb-survey .cb-x { position: absolute; top: 12px; color: #9aa1b1; }
.cb-survey .cb-x:hover { background: #f1f3f7; color: #1f2430; }
.cb-root[dir="rtl"] .cb-survey .cb-x { left: 12px; }
.cb-root[dir="ltr"] .cb-survey .cb-x { right: 12px; }

.cb-q { margin-bottom: 18px; }
.cb-q .cb-qtext { font-weight: 700; font-size: 15px; line-height: 1.55; margin-bottom: 10px; padding-inline-end: 26px; }
.cb-q:not(:first-child) .cb-qtext { padding-inline-end: 0; }
.cb-q .cb-req { color: #b3261e; }
.cb-q .cb-qhint { font-size: 12.5px; color: #6b7280; margin: -4px 0 10px; }

/* follow-up prompt — hidden until the visitor picks a value, then slides open */
.cb-follow { display: none; margin-top: 12px; }
.cb-follow.open { display: block; animation: cb-fadeup .2s ease-out; }
.cb-follow .cb-followlabel { font-size: 13px; color: #4b5563; margin-bottom: 7px; line-height: 1.7; }
.cb-follow .cb-optional { color: #9aa1b1; font-size: 12px; }
@keyframes cb-fadeup { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }

/* choice — radio-style rows */
.cb-opt {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: start;
  border: 1px solid #e2e5ec;
  border-radius: 10px;
  padding: 9px 12px;
  margin-bottom: 7px;
  background: #fff;
  transition: border-color .12s ease, background .12s ease;
}
.cb-opt:hover { border-color: var(--cb-primary); background: color-mix(in srgb, var(--cb-primary) 4%, #fff); }
.cb-opt .cb-radio {
  width: 17px; height: 17px;
  border: 2px solid #c8cdd8;
  border-radius: 50%;
  flex: 0 0 auto;
  display: inline-flex; align-items: center; justify-content: center;
  transition: border-color .12s ease;
}
.cb-opt .cb-radio::after {
  content: '';
  width: 8px; height: 8px;
  border-radius: 50%;
  background: var(--cb-primary);
  transform: scale(0);
  transition: transform .14s ease;
}
.cb-opt.sel { border-color: var(--cb-primary); background: color-mix(in srgb, var(--cb-primary) 7%, #fff); font-weight: 700; }
.cb-opt.sel .cb-radio { border-color: var(--cb-primary); }
.cb-opt.sel .cb-radio::after { transform: scale(1); }

.cb-textarea {
  width: 100%;
  border: 1px solid #d9dce4;
  border-radius: 10px;
  padding: 9px 12px;
  min-height: 76px;
  resize: vertical;
  outline: none;
  background: #fff;
  transition: border-color .12s ease, box-shadow .12s ease;
}
.cb-textarea:focus { border-color: var(--cb-primary); box-shadow: 0 0 0 3px color-mix(in srgb, var(--cb-primary) 14%, transparent); }
.cb-otherbox { margin-top: 4px; min-height: 38px; }

/* form-type question: embedded saved Form fields */
.cb-form { display: flex; flex-direction: column; gap: 13px; }
.cb-formfield { display: flex; flex-direction: column; gap: 6px; }
.cb-flabel { font-size: 13px; font-weight: 600; }
.cb-finput {
  width: 100%;
  border: 1px solid #d9dce4;
  border-radius: 10px;
  padding: 9px 12px;
  outline: none;
  background: #fff;
  font: inherit;
  transition: border-color .12s ease, box-shadow .12s ease;
}
.cb-finput:focus { border-color: var(--cb-primary); box-shadow: 0 0 0 3px color-mix(in srgb, var(--cb-primary) 14%, transparent); }

/* Jalali (Shamsi) date field — trigger that looks like .cb-finput + inline calendar */
.cb-datewrap { position: relative; }
.cb-datetrigger { display: flex; align-items: center; justify-content: space-between; gap: 8px; cursor: pointer; text-align: start; }
.cb-datetrigger svg { flex-shrink: 0; color: #9aa1ad; }
.cb-datetrigger[aria-expanded="true"] { border-color: var(--cb-primary); box-shadow: 0 0 0 3px color-mix(in srgb, var(--cb-primary) 14%, transparent); }
.cb-dateval { font-variant-numeric: tabular-nums; }
.cb-dateph { color: #9aa1ad; }
.cb-cal { display: none; margin-top: 8px; border: 1px solid #e2e5ec; border-radius: 12px; padding: 10px; background: #fff; box-shadow: 0 8px 24px rgba(15, 23, 42, .12); }
.cb-cal.open { display: block; }
.cb-cal-head { display: flex; align-items: center; justify-content: space-between; gap: 6px; margin-bottom: 8px; }
.cb-cal-title { font-size: 13.5px; font-weight: 700; text-align: center; flex: 1; padding: 6px 4px; border-radius: 8px; color: #1f2430; transition: background .12s ease; }
.cb-cal-title:hover { background: color-mix(in srgb, var(--cb-primary) 8%, #fff); }
.cb-cal-nav { width: 30px; height: 30px; display: inline-flex; align-items: center; justify-content: center; border-radius: 8px; color: #4b5563; transition: background .12s ease, color .12s ease; }
.cb-cal-nav:hover { background: color-mix(in srgb, var(--cb-primary) 8%, #fff); color: var(--cb-primary); }
/* month / year picker grids (3 × 4) */
.cb-cal-pick { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; margin-top: 2px; }
.cb-cal-cell { padding: 11px 6px; border-radius: 9px; font-size: 12.5px; font-variant-numeric: tabular-nums; color: #1f2430; transition: background .12s ease, color .12s ease; }
.cb-cal-cell:hover { background: color-mix(in srgb, var(--cb-primary) 10%, #fff); }
.cb-cal-cell.today { box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--cb-primary) 45%, #fff); font-weight: 700; }
.cb-cal-cell.sel { background: var(--cb-primary); color: #fff; font-weight: 700; }
.cb-cal-weekdays, .cb-cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; }
.cb-cal-weekdays { margin-bottom: 4px; }
.cb-cal-weekdays span { text-align: center; font-size: 11px; font-weight: 600; color: #9aa1ad; padding: 2px 0; }
.cb-cal-blank { aspect-ratio: 1; }
.cb-cal-day { aspect-ratio: 1; display: inline-flex; align-items: center; justify-content: center; border-radius: 8px; font-size: 12.5px; font-variant-numeric: tabular-nums; color: #1f2430; transition: background .12s ease, color .12s ease; }
.cb-cal-day:hover { background: color-mix(in srgb, var(--cb-primary) 10%, #fff); }
.cb-cal-day.today { box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--cb-primary) 45%, #fff); font-weight: 700; }
.cb-cal-day.sel { background: var(--cb-primary); color: #fff; font-weight: 700; }
.cb-cal-foot { display: flex; justify-content: space-between; gap: 8px; margin-top: 8px; padding-top: 8px; border-top: 1px solid #eef0f4; }
.cb-cal-foot-btn { font-size: 12.5px; font-weight: 600; color: var(--cb-primary); padding: 4px 8px; border-radius: 7px; transition: background .12s ease; }
.cb-cal-foot-btn:hover { background: color-mix(in srgb, var(--cb-primary) 8%, #fff); }
.cb-cal-clear { color: #6b7280; }

.cb-check { display: flex; align-items: center; gap: 8px; font-size: 13.5px; cursor: pointer; padding: 3px 0; }
.cb-check input { width: 16px; height: 16px; accent-color: var(--cb-primary); flex-shrink: 0; }

/* rating — emoji face scale (worst → best, always LTR like a number line) */
.cb-faces { display: flex; gap: 10px; direction: ltr; justify-content: flex-start; }
.cb-face {
  font-size: 27px;
  line-height: 1;
  width: 44px; height: 44px;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 50%;
  filter: grayscale(1);
  opacity: .5;
  transform: scale(.92);
  transition: filter .12s ease, opacity .12s ease, transform .12s ease, background .12s ease;
}
.cb-face:hover { filter: none; opacity: 1; transform: scale(1.12); }
.cb-face.sel {
  filter: none;
  opacity: 1;
  transform: scale(1.12);
  background: color-mix(in srgb, var(--cb-primary) 10%, #fff);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--cb-primary) 35%, #fff);
}
.cb-face:focus-visible { outline: 2px solid var(--cb-primary); outline-offset: 2px; }

.cb-nps { display: flex; flex-wrap: wrap; gap: 4px; direction: ltr; }
.cb-npsbtn {
  flex: 1 1 0;
  min-width: 28px; height: 34px;
  border: 1px solid #e2e5ec;
  border-radius: 8px;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  background: #fff;
  transition: border-color .12s ease, background .12s ease;
}
.cb-npsbtn:hover { border-color: var(--cb-primary); background: color-mix(in srgb, var(--cb-primary) 6%, #fff); }
.cb-npsbtn.sel { background: var(--cb-primary); border-color: var(--cb-primary); color: #fff; font-weight: 700; }
.cb-npslabels { display: flex; justify-content: space-between; font-size: 11px; color: #6b7280; margin-top: 6px; direction: ltr; }

/* footer: inline primary + quiet close, like a proper dialog */
.cb-actions { display: flex; align-items: center; gap: 6px; margin-top: 2px; }
.cb-submit {
  background: var(--cb-primary);
  color: #fff;
  border-radius: 9px;
  padding: 9px 22px;
  font-weight: 700;
  transition: filter .12s ease;
}
.cb-submit:hover { filter: brightness(1.08); }
.cb-submit:disabled { opacity: .55; cursor: default; filter: none; }
.cb-ghost { color: #6b7280; padding: 9px 14px; border-radius: 9px; font-weight: 600; }
.cb-ghost:hover { background: #f1f3f7; color: #1f2430; }
.cb-formerr { color: #b3261e; font-size: 12px; margin: 0 0 10px; }
.cb-formerr:empty { display: none; }

.cb-thanks { text-align: center; padding: 26px 8px 22px; }
.cb-thanks .cb-thanksicon {
  width: 46px; height: 46px;
  margin: 0 auto 12px;
  border-radius: 50%;
  background: #e8f6ee;
  color: #1e9e57;
  display: flex; align-items: center; justify-content: center;
  animation: cb-pop .25s ease-out;
}
.cb-thanks .cb-thankstext { font-weight: 700; font-size: 15px; }

/* ---------- mobile: full-height sheet ---------- */
@media (max-width: 480px) {
  .cb-panel {
    /* !important overrides the inline right/left side offset (set in chat.ts)
       that would otherwise pin the full-screen sheet 20px off one edge */
    inset: 0 !important;
    width: 100%;
    max-width: none;
    height: 100%;
    max-height: none;
    border-radius: 0;
    padding-bottom: env(safe-area-inset-bottom);
  }
  .cb-survey.slide_in {
    left: 12px !important;
    right: 12px !important;
    width: auto;
    bottom: 12px;
    padding-bottom: calc(20px + env(safe-area-inset-bottom));
  }
}

@media (prefers-reduced-motion: reduce) {
  .cb-panel, .cb-survey, .cb-backdrop, .cb-launcher { animation: none; transition: none; }
}
`;
